import React, { useMemo, useState, useEffect } from 'react';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { Text } from 'src/modules/shared/components/atoms/text';

export type MappingValue =
  | { kind: 'event'; path: string }
  | {
      kind: 'literal';
      literalType: 'string' | 'number' | 'boolean' | 'json' | 'null';
      value: string;
    }
  | { kind: 'empty' };

interface MappingRowProps {
  fieldKey: string;
  required: boolean;
  value?: MappingValue;
  onChange: (value: MappingValue) => void;
  eventPathOptions?: string[];
  expectedLiteralType?: 'string' | 'number' | 'boolean' | 'json' | 'null';
}

export const MappingRow: React.FC<MappingRowProps> = ({
  fieldKey,
  required,
  value,
  onChange,
  eventPathOptions,
  expectedLiteralType,
}) => {
  const [mode, setMode] = useState<'event' | 'literal' | 'empty'>(
    value?.kind || (required ? 'event' : 'empty')
  );

  const [eventPath, setEventPath] = useState<string>(
    value?.kind === 'event' ? value.path : ''
  );
  const [literalType, setLiteralType] = useState<
    'string' | 'number' | 'boolean' | 'json' | 'null'
  >(
    value?.kind === 'literal'
      ? value.literalType
      : expectedLiteralType || 'string'
  );
  const [literalValue, setLiteralValue] = useState<string>(
    value?.kind === 'literal' ? value.value : ''
  );
  const finalLiteralType: 'string' | 'number' | 'boolean' | 'json' | 'null' =
    expectedLiteralType || literalType;

  // Sync internal state when the parent-provided value changes (e.g., prefilled mappings).
  useEffect(() => {
    if (!value) {
      setMode(required ? 'event' : 'empty');
      setEventPath('');
      setLiteralType(expectedLiteralType || 'string');
      setLiteralValue('');
      return;
    }
    setMode(
      value.kind === 'empty' ? (required ? 'event' : 'empty') : value.kind
    );
    if (value.kind === 'event') {
      setEventPath(value.path);
    } else if (value.kind === 'literal') {
      setLiteralType(value.literalType);
      setLiteralValue(value.value);
    }
  }, [value, required, expectedLiteralType]);

  const literalTypeOptions = useMemo(
    () => [
      { value: 'string', content: 'string' },
      { value: 'number', content: 'number' },
      { value: 'boolean', content: 'boolean' },
      { value: 'json', content: 'json' },
      { value: 'null', content: 'null' },
    ],
    []
  );

  const modeOptions = useMemo(() => {
    const base = [
      { value: 'event', content: 'Evento' },
      { value: 'literal', content: 'Literal' },
    ];
    if (!required) base.push({ value: 'empty', content: 'Vacío' });
    return base;
  }, [required]);

  const handleModeChange = (next: 'event' | 'literal' | 'empty') => {
    setMode(next);
    if (next === 'event') {
      onChange({ kind: 'event', path: eventPath });
    } else if (next === 'literal') {
      const nextType =
        value?.kind === 'literal'
          ? finalLiteralType
          : expectedLiteralType || 'string';
      if (!expectedLiteralType) setLiteralType(nextType);
      onChange({ kind: 'literal', literalType: nextType, value: literalValue });
    } else {
      onChange({ kind: 'empty' });
    }
  };

  const handleEventPathChange = (path: string) => {
    setEventPath(path);
    onChange({ kind: 'event', path });
  };

  const handleLiteralTypeChange = (
    t: MappingRowProps['value'] extends never
      ? never
      : 'string' | 'number' | 'boolean' | 'json' | 'null'
  ) => {
    setLiteralType(t);
    onChange({ kind: 'literal', literalType: t, value: literalValue });
  };

  const handleLiteralValueChange = (val: string) => {
    setLiteralValue(val);
    onChange({ kind: 'literal', literalType: finalLiteralType, value: val });
  };

  return (
    <div className="flex items-start gap-2">
      <div className="w-48">
        <Text size="tiny" className="text-gray-600">
          {fieldKey}
        </Text>
        {required && (
          <Text size="tiny" className="text-danger-600">
            * requerido
          </Text>
        )}
      </div>

      <div className="w-32">
        <Select
          value={mode}
          onChange={(e) =>
            handleModeChange(
              (e.target as HTMLSelectElement).value as typeof mode
            )
          }
          aria-label={`Modo de mapeo para ${fieldKey}`}
          options={modeOptions}
        />
      </div>

      {mode === 'event' && (
        <div className="flex-1">
          {eventPathOptions && eventPathOptions.length > 0 ? (
            <SearchableSelect
              value={eventPath}
              onChange={(val) => handleEventPathChange(val ?? '')}
              aria-label={`Ruta del evento para ${fieldKey}`}
              size="lg"
              className="w-full"
              containerClassName="w-full"
              options={[
                ...(eventPath && !eventPathOptions.includes(eventPath)
                  ? [{ value: eventPath, content: eventPath }]
                  : []),
                ...eventPathOptions.map((p) => ({ value: p, content: p })),
              ]}
            />
          ) : (
            <TextInput
              value={eventPath}
              onChange={(e) =>
                handleEventPathChange((e.target as HTMLInputElement).value)
              }
              aria-label={`Ruta del evento para ${fieldKey}`}
              placeholder="path.en.evento (p.ej. contact.id)"
            />
          )}
          <Text size="tiny" className="text-gray-500">
            {eventPathOptions && eventPathOptions.length > 0
              ? 'Selecciona un campo del evento' // TODO: check when implementing i18n
              : 'Ruta con puntos del contenido del evento'}
          </Text>
        </div>
      )}

      {mode === 'literal' && (
        <div className="flex-1 flex items-start gap-2">
          {expectedLiteralType ? (
            <div className="w-28">
              <Text size="tiny" className="text-neutral-600 mt-2">
                Tipo: {finalLiteralType}{' '}
                {/* TODO: check when implementing i18n */}
              </Text>
            </div>
          ) : (
            <div className="w-28">
              <Select
                value={finalLiteralType}
                onChange={(e) =>
                  handleLiteralTypeChange(
                    (e.target as HTMLSelectElement).value as typeof literalType
                  )
                }
                aria-label={`Tipo literal para ${fieldKey}`}
                options={literalTypeOptions}
              />
            </div>
          )}
          {finalLiteralType !== 'null' && (
            <div className="flex-1">
              <TextInput
                value={literalValue}
                onChange={(e) =>
                  handleLiteralValueChange((e.target as HTMLInputElement).value)
                }
                aria-label={`Valor literal para ${fieldKey}`}
                placeholder={
                  finalLiteralType === 'json'
                    ? '{"key":"value"}'
                    : finalLiteralType === 'boolean'
                      ? 'true | false'
                      : finalLiteralType === 'number'
                        ? '42'
                        : 'texto'
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MappingRow;
